<template>
  <NodePage
    v-if="node"
    v-bind="node"
    :class="{
      'cursor-wait': loadingData,
    }"
  >
    <template #header-after>
      <Container>
        <div
          ref="scrollToEl"
          class="bg-white flex mb-40 scroll-mt-40 items-start lg:items-center flex-col lg:flex-row"
        >
          <NuxtLink
            v-for="tab in tabs"
            :key="tab.id"
            class="flex lg:justify-center py-10 px-20 lg:py-20 lg:px-30 text-base font-medium uppercase text-berry lg:border-r lg:border-r-gray-300 items-center"
            :class="tab.colorClass"
            :to="tab.to"
          >
            <span
              class="relative place-content-center grid h-20 w-20 mr-10 bg-white ring-1 ring-gray-500 peer-checked:ring-green-alt text-transparent peer-checked:text-green-alt hover:ring-gray-900 peer-focus:ring-3 peer-focus:ring-green-alt peer-focus:ring-offset-3 rounded"
              aria-hidden="true"
              :class="[
                tab.colorClass,
                targetGroup === tab.id
                  ? 'ring-green-alt before:bg-green-alt before:size-10 before:block before:rounded '
                  : '',
              ]"
            />
            {{ tab.text }}
          </NuxtLink>

          <div v-if="options" class="flex-grow-0">
            <div class="ml-auto w-full">
              <FormKit
                v-model="productType"
                type="select"
                name="filter"
                :options="options"
                outer-class="shop-select"
              />
            </div>
          </div>
        </div>

        <!-- Tabs content -->

        <div
          :class="{
            'opacity-25 pointer-events-none': loadingData,
          }"
          class="grid gap-20 lg:gap-30 xl:gap-50"
        >
          <div v-if="!loadingData && !products?.length" class="text-center">
            {{
              $texts('shop.noProducts')
            }}
          </div>
          <template v-else>
            <CommerceProduct
              v-for="product in products"
              v-bind="product"
              :key="product.id"
            />
          </template>
        </div>
        <div
          v-if="totalPages > 1"
          class="bg-gray-50 md:sticky md:bottom-0 mt-20 md:py-25 md:px-10 md:-mx-10"
        >
          <Pagination
            :page="page"
            :total-pages="totalPages"
            @change-page="onChangePage"
          />
        </div>
      </Container>
    </template>
  </NodePage>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'
import type { NuxtSvgSpriteSymbol } from '#nuxt-svg-icon-sprite/runtime'
import type {
  CommerceProductTeaserFragment,
  ShopQueryVariables,
} from '#graphql-operations'
import { EntityQueryOperator } from '#graphql-operations'
import Pagination from '~/components/Pagination/index.vue'
import { useDrupalUser } from '~/stores/drupalUser'
import { falsy } from '#shared/helpers/type'
import type { Option } from '~/types/option'

await initFormKit()

definePageMeta({
  name: 'shop',
  drupalRoute: true,
  drupalFrontendRoute: true,
  path: '/dienstleistungen/webshop',
  languageMapping: {
    fr: '/prestations/boutique-en-ligne',
    it: '/servizi/webshop',
  },
})

const nuxtRoute = useRoute()
const drupalUser = useDrupalUser()
const router = useRouter()
const { $texts } = useNuxtApp()

type ShopTargetGroup = 'pharmacists' | 'pharmacies'

type ShopTargetGroupTab = {
  id: ShopTargetGroup
  text: string
  icon: NuxtSvgSpriteSymbol
  colorClass: string
  to: RouteLocationRaw
}

const tabs = computed<ShopTargetGroupTab[]>(() => {
  return [
    {
      id: 'pharmacies',
      text: $texts('shop.productsForPharmacies'),
      icon: 'pharmasuisse',
      colorClass: 'text-berry',
      to: {
        path: nuxtRoute.path,
        query: {
          targetGroup: 'pharmacies',
          productType: productType.value || undefined,
        },
      },
    },
    {
      id: 'pharmacists',
      text: $texts('shop.productsForPharmacists'),
      icon: 'individual_member',
      colorClass: 'text-orange',
      to: {
        path: nuxtRoute.path,
        query: {
          targetGroup: 'pharmacists',
          productType: productType.value || undefined,
        },
      },
    },
  ]
})

const productType = computed<string>({
  get() {
    const v = nuxtRoute.query.productType
    return typeof v === 'string' ? v : ''
  },
  set(productType: string) {
    const currentQuery = { ...router.currentRoute.value.query }

    if (!productType) {
      return router.push({
        query: { ...currentQuery, productType: undefined },
        path: nuxtRoute.path,
      })
    }

    return router.push({
      query: { ...currentQuery, productType },
      path: nuxtRoute.path,
    })
  },
})

const targetGroup = computed<ShopTargetGroup>(() => {
  const v = nuxtRoute.query.targetGroup
  if (typeof v === 'string' && (v === 'pharmacies' || v === 'pharmacists')) {
    return v
  }
  if (drupalUser.isAffiliatedPharmacy || drupalUser.isNonAffiliatedPharmacy) {
    return 'pharmacies'
  }

  return 'pharmacists'
})

const roles = computed<string[]>(() => {
  if (targetGroup.value === 'pharmacies') {
    return ['affiliated_pharmacy', 'non_affiliated_pharmacy']
  }

  return ['individual_member', 'non_member']
})

const { entity: node } = await useDrupalRouteQuery('nodePage')

// The amount of items per page.
const perPage = 20

// The current page for the pagination.
const page = computed<number>(() => {
  if (typeof nuxtRoute.query.page === 'string') {
    return parseInt(nuxtRoute.query.page)
  }

  return 0
})

const variables = computed<ShopQueryVariables>(() => {
  return {
    offset: page.value * perPage,
    perPage,
    roles: roles.value,
    productType: productType.value,
    operator: productType.value
      ? EntityQueryOperator.IN
      : EntityQueryOperator.NOT_IN,
  }
})

// Load the products.
const { data, pending: loadingData } = await useAsyncGraphqlQuery(
  'shop',
  variables,
  {
    transform: (v) => v.data.entityQuery,
    graphqlCaching: {
      client: true,
    },
  },
)

const products = computed<CommerceProductTeaserFragment[]>(() => {
  return (data.value?.items || []).filter(
    (v) => v && 'title' in v,
  ) as CommerceProductTeaserFragment[]
})

const totalPages = computed<number>(() => {
  return Math.ceil((data.value?.total || 0) / perPage)
})

// Setup filters
const { data: optionsData } = await useAsyncGraphqlQuery(
  'taxonomyProductTypes',
  null,
  {
    transform: (v) =>
      v.data.entityQuery.items
        ?.map((v) => (v && 'value' in v ? v : null))
        .filter(falsy),
    graphqlCaching: {
      client: true,
    },
  },
)

const options = computed<Option[]>(() => {
  return [
    {
      label: $texts('shop.allProducts'),
      value: '',
    },
    ...(optionsData.value || []),
  ]
    .map((v) => {
      if (v.value !== undefined && v.label !== undefined) {
        return {
          label: v.label,
          value: v.value,
        }
      }
      return null
    })
    .filter(falsy)
})

const scrollToEl = ref<HTMLDivElement | null>(null)
const { onChangePage } = usePaginationChange(scrollToEl)

useRouteCache((c) => c.setCacheable().setMaxAge(600))
await renderPageDependencies()
</script>

<script lang="ts">
export default {
  name: 'PagePharmasuisseShop',
}
</script>

<style lang="postcss">
/* specific styling overrides for the select element in the shop filters */

.shop-select {
  @apply pl-15;
  .formkit-inner {
    @apply ring-0 inline-flex mb-0;
  }
}
</style>
